import { useState } from "react";
import { Box, Button, Dialog, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { Add, Close, Description, Link as LinkIcon, MoreVert, Notes } from "@mui/icons-material";
import { format } from "date-fns";
import { API_URL } from "../../../../Apis/config";
import { DELETE_REFERENCE } from "../../../../Apis/order_api";
import { ToastShow } from "../../../../Component/Toast/Toast";
import ConfirmationDailog from "../../../../Component/Dailog/ConfirmationDailog";
import AddReferenceDialog from "./AddReferenceDialog";

// type: 1 text, 2 link, 3 image, 4 document, 5 audio (orders_references)
const TYPE_LABEL = { 1: "Text", 2: "Link", 3: "Image", 4: "Document", 5: "Audio" };

const formatDate = (value) => {
  const d = new Date(String(value ?? "").replace(" ", "T"));
  return isNaN(d.getTime()) ? "" : format(d, "dd MMM yyyy, hh:mm a");
};

/**
 * Order details → "Additional References".
 * Text, links, images and documents added by the customer or the POD team (during booking or later, until delivered).
 * While the order status is 0–6, admin can add, edit and delete any reference; delivered/cancelled → read-only.
 * Same look as the "Note" box.
 */
const AdditionalReferencesTable = ({ references = [], ordersId, orderStatus, onChanged }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dialog, setDialog] = useState(null); // { editing: ref | null }
  const [menu, setMenu] = useState(null); // { anchor, ref }
  const [toDelete, setToDelete] = useState(null);

  const canEdit = orderStatus >= 0 && orderStatus <= 6;
  if (!canEdit && references.length === 0) return null;

  const handleDelete = async (v) => {
    const ref = toDelete;
    setToDelete(null);
    if (!v || !ref) return;
    const res = await DELETE_REFERENCE(ref.id);
    if (res && res.s == 1) {
      ToastShow("Reference deleted");
      onChanged && onChanged();
    } else {
      ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
    }
  };

  const fileUrl = (r) => API_URL.baseUrl + API_URL.OrderReference + r.file;

  const renderContent = (r) => {
    if (r.type === 1) {
      return (
        <Typography fontSize="13px" fontWeight="500" sx={{ whiteSpace: "pre-wrap" }}>
          {r.description}
        </Typography>
      );
    }
    if (r.type === 2) {
      return (
        <Typography
          component="a"
          href={r.link}
          target="_blank"
          rel="noopener noreferrer"
          fontSize="13px"
          fontWeight="500"
          color="primary"
          sx={{ wordBreak: "break-all", display: "flex", alignItems: "center", gap: "0.3rem" }}
        >
          <LinkIcon fontSize="small" />
          {r.link}
        </Typography>
      );
    }
    if (r.type === 5) {
      // Browser player (play / pause / seek / download). .ogg doesn't play in Safari; it can still be downloaded.
      return (
        <Box
          component="audio"
          controls
          preload="none"
          src={fileUrl(r)}
          sx={{ width: "100%", maxWidth: "420px", height: "36px", display: "block", pr: "2rem" }}
        />
      );
    }
    if (r.type === 3) {
      return (
        <Box
          component="img"
          src={fileUrl(r)}
          alt={r.file_name || "Reference image"}
          onClick={() => setPreviewUrl(fileUrl(r))}
          sx={{
            width: "90px",
            height: "90px",
            objectFit: "cover",
            borderRadius: "5px",
            cursor: "pointer",
            border: "1px solid #EFEFEF",
          }}
        />
      );
    }
    return (
      <Typography
        component="a"
        href={fileUrl(r)}
        target="_blank"
        rel="noopener noreferrer"
        fontSize="13px"
        fontWeight="500"
        color="primary"
        sx={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
      >
        <Description fontSize="small" />
        {r.file_name || r.file}
      </Typography>
    );
  };

  return (
    <Box
      width="100%"
      boxShadow="3px 3px 7px 0px #00000040"
      sx={{ borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }}
    >
      {/*Header*/}
      <Box
        sx={{
          width: "100%",
          bgcolor: "#FFAB30",
          height: "2.370rem",
          borderTopLeftRadius: "5px",
          borderTopRightRadius: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography fontSize="15px" fontWeight="500" ml="0.8rem">
          Additional References ({references.length})
        </Typography>
        {canEdit ? (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => setDialog({ editing: null })}
            sx={{
              mr: "0.5rem",
              textTransform: "none",
              color: "black",
              bgcolor: "white",
              "&:hover": { bgcolor: "#FFF4E0" },
            }}
          >
            Add
          </Button>
        ) : null}
      </Box>

      {references.length === 0 ? (
        <Box
          bgcolor="white"
          sx={{ p: "0.8rem", borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }}
        >
          <Typography fontSize="13px" color="#7A7A7A">
            No references added yet.
          </Typography>
        </Box>
      ) : null}

      {/*Rows (newest first)*/}
      {references.map((r, i) => {
        const isLast = i === references.length - 1;
        const edited =
          r.updated_at && r.created_at && formatDate(r.updated_at) !== formatDate(r.created_at);
        return (
          <Stack
            key={r.id}
            direction="row"
            bgcolor="white"
            sx={
              isLast
                ? { borderBottomLeftRadius: "5px", borderBottomRightRadius: "5px" }
                : undefined
            }
          >
            <Box
              sx={{
                bgcolor: "#EFEFEF",
                width: "8.8vw",
                minWidth: "8.8vw",
                pl: "0.8rem",
                paddingY: "0.5rem",
                borderBottom: isLast ? "none" : "1px solid white",
                display: "flex",
                alignItems: "flex-start",
                gap: "0.3rem",
              }}
            >
              {r.type === 1 ? <Notes sx={{ fontSize: "16px" }} /> : null}
              <Typography fontSize="13px">{TYPE_LABEL[r.type] ?? "Reference"}</Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                pl: "0.8rem",
                pr: "0.8rem",
                paddingY: "0.5rem",
                bgcolor: "white",
                borderBottom: isLast ? "none" : "1px solid #EFEFEF",
                position: "relative",
              }}
            >
              {canEdit ? (
                <IconButton
                  size="small"
                  onClick={(e) => setMenu({ anchor: e.currentTarget, ref: r })}
                  sx={{ position: "absolute", top: 4, right: 4 }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              ) : null}
              {renderContent(r)}
              {r.type !== 1 && r.description ? (
                <Typography fontSize="12px" mt="0.3rem" sx={{ whiteSpace: "pre-wrap" }}>
                  {r.description}
                </Typography>
              ) : null}
              <Typography fontSize="11px" color="#7A7A7A" mt="0.3rem">
                {formatDate(r.created_at)}
                {edited ? " (edited)" : ""}
                {" · Added by "}
                {r.added_by_app === 3 ? "POD team" : "customer"}
              </Typography>
            </Box>
          </Stack>
        );
      })}

      {/*Row menu*/}
      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setDialog({ editing: menu.ref });
            setMenu(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setToDelete(menu.ref);
            setMenu(null);
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {dialog ? (
        <AddReferenceDialog
          ordersId={ordersId}
          editing={dialog.editing}
          onClose={(changed) => {
            setDialog(null);
            if (changed && onChanged) onChanged();
          }}
        />
      ) : null}

      {toDelete ? (
        <ConfirmationDailog
          open
          title="Delete reference?"
          content="Are you sure you want to delete this reference? The customer and podian will no longer see it."
          handleOnClose={handleDelete}
        />
      ) : null}

      {/*Image preview*/}
      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="lg">
        <Box sx={{ position: "relative", bgcolor: "black" }}>
          <IconButton
            onClick={() => setPreviewUrl(null)}
            sx={{ position: "absolute", top: 4, right: 4, color: "white" }}
          >
            <Close />
          </IconButton>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Reference"
              sx={{ display: "block", maxWidth: "90vw", maxHeight: "85vh" }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdditionalReferencesTable;
